import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for resizable split screen sidebars (WhatsApp Web style).
 * Resets to defaultWidth on component mount / page refresh (does not save to localStorage).
 */
export const useResizableSidebar = (defaultWidth = 380, minWidth = 260, maxWidth = 600) => {
  const [sidebarWidth, setSidebarWidth] = useState(defaultWidth);
  const [isDragging, setIsDragging] = useState(false);

  // Start dragging handler (Mouse or Touch)
  const startDragging = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (clientX) => {
      // Calculate allowed max width (capped at 50% of viewport width)
      const dynamicMax = Math.min(maxWidth, window.innerWidth * 0.5);
      const clampedWidth = Math.max(minWidth, Math.min(clientX, dynamicMax));
      setSidebarWidth(clampedWidth);
    };

    const onMouseMove = (e) => {
      handleMove(e.clientX);
    };

    const onTouchMove = (e) => {
      if (e.touches && e.touches[0]) {
        handleMove(e.touches[0].clientX);
      }
    };

    const stopDragging = () => {
      setIsDragging(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    // Apply cursor & selection locks during drag
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', stopDragging);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', stopDragging);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopDragging);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', stopDragging);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, minWidth, maxWidth]);

  return {
    sidebarWidth,
    isDragging,
    startDragging
  };
};

export default useResizableSidebar;
